import type { Draft, PayloadAction, Slice, SliceSelectors } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { placeOrder } from "@store/slices/LEGACY__checkoutSlice/actions/placeOrder";
import type { RootStoreState } from "@store/types";
import { mapProductToCommerceEventItem } from "@store/utils";
import { clearFreeTrialRedirect, isFreeTrialRedirect, redirectToTrialAuthWithBack } from "@store/utils/cartHelpers";
import { fromMagentoCurrency } from "@store/utils/fromMagentoCurrency";
import { parseAddToCartErrorMessage } from "@store/utils/parseAddToCartErrorMessage";

import { newrelic } from "@jti/fe-clients";
import { customPaymentMethods } from "@jti/fe-common";
import { getMessageBus } from "@jti/fe-message-bus";
import type { Product } from "@jti/magento";
import { magento } from "@jti/magento";
import { MagentoGraphQLRequestClientError } from "@jti/magento-api-v2";
import { calculateProductPoints, getUser } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { addProductToCart } from "./actions/addProductToCart";
import { fetchCart, fetchTrialCart } from "./actions/fetchCart";
import { fetchCartItems, fetchCleanCartItems } from "./actions/fetchCartItems";
import { fetchCartPrices } from "./actions/fetchCartPrices";

import type { CartSliceState, CartStoreConfig, DiscontinuedProductError, FreeGiftError } from "./cartSlice.types";

const messageBus = getMessageBus();

export const removeItem = createAsyncThunk(
  "cart/removeItem",
  async ({ cartId, item }: { cartId: string; item: Product }) => {
    const eventData = {
      currency: item.price.currency,
      value: item.price.value,
      ecommerce: {
        items: [mapProductToCommerceEventItem(item, 0)],
      },
    };

    dispatchTrackingEvent(TrackingEvents.TrackingCartQuantityRemove, eventData);

    return magento.removeItemFromCart(cartId, item.id ?? "");
  }
);

export const removeItems = createAsyncThunk(
  "cart/removeItems",
  async ({ cartId, itemIds }: { cartId: string; itemIds: string[] }) => magento.removeItemsFromCart(cartId, itemIds)
);

export const updateItemQuantity = createAsyncThunk(
  "cart/updateItemQuantity",
  async ({ cartId, itemId, quantity }: { cartId: string; itemId: string; quantity: number }) => {
    if (!!quantity || quantity === 0) {
      return magento.updateCartItemQuantity(cartId, itemId, quantity);
    }
  }
);

export const fetchCoupons = createAsyncThunk("cart/fetchCoupons", async (cartId: string) =>
  magento.getCustomerCoupons(cartId)
);

export const applyCoupons = createAsyncThunk(
  "cart/applyCoupons",
  async ({ cartId, couponCode }: { cartId: string; couponCode: string[] }, { rejectWithValue }) => {
    try {
      const result = await magento.applyCouponsToCart(cartId, couponCode);

      return {
        appliedCoupons:
          result?.applyCouponsToCart?.cart?.applied_coupons?.filter(
            (coupon): coupon is { code: string; label: string } => !!coupon?.code
          ) ?? [],
      };
    } catch (error) {
      if (MagentoGraphQLRequestClientError.isMagentoGraphQLRequestClientError(error)) {
        const message = error.errorsDetails?.[0]?.message || error.message;
        return rejectWithValue(message);
      }
      const message = error instanceof Error ? error.message : "";
      return rejectWithValue(message);
    }
  }
);

export const removeCoupons = createAsyncThunk(
  "cart/removeCoupons",
  async ({ cartId, couponCodes }: { cartId: string; couponCodes: string[] }) => {
    await magento.removeCouponsFromCart(cartId, couponCodes);
    return couponCodes;
  }
);

export const fetchCurrency = createAsyncThunk("cart/fetchCurrency", async () => {
  try {
    return await magento.getCurrency();
  } catch (error) {
    newrelic.noticeError(error as Error, { context: "cart/fetchCurrency" });
    throw error;
  }
});

export const fetchStoreConfig = createAsyncThunk("cart/fetchStoreConfig", async () => {
  try {
    return await magento.getStoreConfig();
  } catch (error) {
    newrelic.noticeError(error as Error, { context: "cart/fetchStoreConfig" });
    throw error;
  }
});

const shouldInitializeCart = ({ id, isFreeTrial, isLoadingCartData }: CartSliceState) =>
  !isLoadingCartData && (!id || isFreeTrial === undefined);

const getCartInitializationState = (cart: CartSliceState) => ({
  existingCartId: cart.id,
  isFreeTrial: cart.isFreeTrial ?? isFreeTrialRedirect(),
});

export const initializeCart = createAsyncThunk<void, string, { state: RootStoreState }>(
  "cart/initializeCart",
  async (trialLoginHref, { dispatch, getState }) => {
    const { isFreeTrial, existingCartId } = getCartInitializationState(getState().cart);

    const initializeFreeTrialCart = async () => {
      dispatch(cartSlice.actions.setIsFreeTrial(isFreeTrial));

      if (!isFreeTrial) {
        return;
      }

      if (getUser()?.magento_token) {
        await dispatch(fetchTrialCart({ useAnonymous: true })).unwrap();
        return;
      }

      clearFreeTrialRedirect();
      redirectToTrialAuthWithBack(trialLoginHref);
    };

    const initializeStandardCart = async () => {
      if (existingCartId) {
        return;
      }

      const { currency, storeConfig } = getState().cart;

      if (!currency.code) {
        dispatch(fetchCurrency());
      }
      if (!storeConfig?.store_group_code) {
        await dispatch(fetchStoreConfig()).unwrap();
      }

      await dispatch(fetchCart({ useAnonymous: true })).unwrap();
    };

    await initializeFreeTrialCart();
    await initializeStandardCart();
  },
  {
    condition: (_, { getState }) => shouldInitializeCart(getState().cart),
  }
);

export const initialCartState: CartSliceState = {
  id: "",
  items: [],
  oldItems: [],
  countries: [],
  prices: {
    fetchStatus: "idle",
    data: null,
  },
  isZeroValueOrder: false,
  isFailedPaymentFlow: false,
  isSubscription: false,
  isFreeTrial: undefined,
  minimumPayableAmount: undefined,
  coupons: [],
  couponsPending: false,
  availablePaymentMethods: [],
  itemsPending: true,
  pendingItemsCartId: null,
  qtyPending: false,
  notLoggedIn: false,
  couponErrors: [],
  order: {
    orderDetails: {
      fetchStatus: "idle",
      data: null,
    },
    points: 0,
  },
  currency: {
    code: "",
    symbol: "",
  },
  fetchCurrencyStatus: "idle",
  promoCode: {
    isVisible: true,
    isLimitReached: false,
  },
  fetchStoreConfigStatus: "idle",
  storeConfig: {
    base_currency_code: "",
    is_order_limit_enabled: false,
    is_age_verification_enabled: false,
    order_limit_value: 0,
    store_code: "",
    store_group_code: "",
    is_free_gifts_enabled: false,
    gift_basket_quantity_limitation: "0",
    postcode_is_required: true,
    postcode_max_length: 0,
    postcode_min_length: 0,
    postcode_validate_regex: "",
    postcode_validation_enabled: false,
    allow_empty_firstname: false,
    cartons_limit_value: 0,
  },
  isLoadingCartData: false,
  availableFreeGifts: [],
};

export const cartSlice: Slice<
  CartSliceState,
  {
    clearCouponErrors(state: Draft<CartSliceState>): void;
    setIsZeroValueOrder(state: Draft<CartSliceState>, action: PayloadAction<boolean>): void;
    setIsFailedPaymentFlow(state: Draft<CartSliceState>, action: PayloadAction<boolean>): void;
    setIsFreeTrial(state: Draft<CartSliceState>, action: PayloadAction<boolean>): void;
    setTradeInErrorMessage(
      state: Draft<CartSliceState>,
      action: PayloadAction<CartSliceState["tradeInErrorMessage"]>
    ): void;
    setFreeGiftsErrors(state: Draft<CartSliceState>, action: PayloadAction<FreeGiftError[]>): void;
    clearFreeGiftsErrors(state: Draft<CartSliceState>): void;
    setDiscontinuedProductsErrors(
      state: Draft<CartSliceState>,
      action: PayloadAction<DiscontinuedProductError[]>
    ): void;
    clearDiscontinuedProductsErrors(state: Draft<CartSliceState>): void;
  },
  "cart",
  "cart",
  SliceSelectors<CartSliceState>
> = createSlice({
  name: "cart",
  initialState: initialCartState,
  reducers: {
    clearCouponErrors(state) {
      state.couponErrors = [];
    },
    setIsZeroValueOrder(state, action) {
      state.isZeroValueOrder = action.payload;
    },
    setIsFailedPaymentFlow(state, action) {
      state.isFailedPaymentFlow = action.payload;

      if (action.payload) {
        state.isZeroValueOrder = false;
      }
    },
    setIsFreeTrial(state, action) {
      state.isFreeTrial = action.payload;
    },
    setTradeInErrorMessage(state, action) {
      state.tradeInErrorMessage = action.payload;
    },
    setFreeGiftsErrors(state, action) {
      state.freeGiftsErrors = action.payload;
    },
    clearFreeGiftsErrors(state) {
      state.freeGiftsErrors = [];
    },
    setDiscontinuedProductsErrors(state, action) {
      state.discontinuedProductsErrors = action.payload;
    },
    clearDiscontinuedProductsErrors(state) {
      state.discontinuedProductsErrors = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initializeCart.pending, (state) => {
      state.isLoadingCartData = true;
    });

    builder.addCase(initializeCart.fulfilled, (state) => {
      state.isLoadingCartData = false;
    });

    builder.addCase(initializeCart.rejected, (state) => {
      state.isLoadingCartData = false;
    });

    builder.addCase(updateItemQuantity.pending, (state, action) => {
      state.qtyPending = true;
      state.oldItems = [...state.items];
      state.items = state.items.map((item) =>
        item.id === action.meta.arg.itemId
          ? {
              ...item,
              quantity: action.meta.arg.quantity,
            }
          : item
      );
    });

    builder.addCase(updateItemQuantity.fulfilled, (state, action) => {
      state.qtyPending = false;
      if (action.meta.arg.quantity === 0) {
        state.items = state.items.filter((item) => item.id !== action.meta.arg.itemId);
      }

      state.items = state.items.map((item: Product) => ({
        ...item,
        points: calculateProductPoints(item.rules, item.quantity),
      }));
    });

    builder.addCase(updateItemQuantity.rejected, (state) => {
      state.qtyPending = false;

      if (state.oldItems) {
        state.items = [...state.oldItems];
      }
    });

    builder.addCase(fetchCart.fulfilled, (state, action) => {
      state.id = action.payload.id!;
      state.promoCode.isVisible = !!action.payload.can_apply_coupons;
      state.availablePaymentMethods = action.payload.available_payment_methods || [];
      state.storeConfig.free_shipping_threshold = action.payload.free_shipping_threshold ?? null;

      messageBus.publish("cart.loaded", { payload: { cartId: action.payload.id ?? "" } }, { sticky: true });
    });

    builder.addCase(fetchCart.rejected, (state) => {
      state.itemsPending = false;
      state.notLoggedIn = true;
    });

    builder.addCase(fetchCartPrices.rejected, (state) => {
      state.prices.fetchStatus = "error";
    });

    builder.addCase(fetchCartPrices.pending, (state) => {
      state.prices.fetchStatus = "pending";
    });

    builder.addCase(fetchCartPrices.fulfilled, (state, action) => {
      state.prices.data = action.payload.prices;

      const minimumPayableAmount = action.payload.minimum_payable_amount;

      state.minimumPayableAmount = minimumPayableAmount
        ? {
            ...minimumPayableAmount,
            isReached: Boolean(
              minimumPayableAmount.value &&
              state.prices.data.grandTotal?.value &&
              state.prices.data.grandTotal.value <= minimumPayableAmount.value
            ),
          }
        : minimumPayableAmount;

      if (!state.isFailedPaymentFlow) {
        state.availablePaymentMethods = action.payload.available_payment_methods ?? [];
        state.isZeroValueOrder = state.availablePaymentMethods.some(
          (method) => method.code === customPaymentMethods.zeroValueOrder
        );
      }

      state.prices.fetchStatus = "success";
    });

    builder.addCase(fetchCartItems.pending, (state, action) => {
      const silent = typeof action.meta.arg === "object" && action.meta.arg.silent;
      if (!silent) {
        state.itemsPending = true;
        messageBus.publish("cart.fetched.state", { payload: { fetched: false } });
      }
    });

    builder.addCase(fetchCartItems.fulfilled, (state, action) => {
      state.items = action.payload.items;
      state.availableFreeGifts = (action.payload.availableFreeGifts ?? []).filter(Boolean);
      state.itemsPending = false;
      state.isSubscription = action.payload.items.filter((item) => item.subscription === 1).length > 0;

      messageBus.publish("cart.items.fetched", { payload: { items: action.payload.items } });
      messageBus.publish("cart.fetched.state", { payload: { fetched: true } });
    });

    builder.addCase(fetchCartItems.rejected, (state) => {
      state.itemsPending = false;
      messageBus.publish("cart.fetched.state", { payload: { fetched: true } });
    });

    builder.addCase(fetchCleanCartItems.pending, (state, action) => {
      state.itemsPending = true;
      state.pendingItemsCartId = action.meta.arg;
      messageBus.publish("cart.fetched.state", { payload: { fetched: false } });
    });

    builder.addCase(fetchCleanCartItems.fulfilled, (state, action) => {
      state.items = action.payload.items;
      state.availableFreeGifts = (action.payload.availableFreeGifts ?? []).filter(Boolean);
      state.itemsPending = false;
      state.pendingItemsCartId = null;
      messageBus.publish("cart.fetched.state", { payload: { fetched: true } });
      state.isSubscription = action.payload.items.filter((item) => item.subscription === 1).length > 0;
    });

    builder.addCase(fetchCleanCartItems.rejected, (state) => {
      state.itemsPending = false;
      state.pendingItemsCartId = null;
      messageBus.publish("cart.fetched.state", { payload: { fetched: true } });
    });

    builder.addCase(fetchCoupons.pending, (state) => {
      state.couponsPending = true;
    });

    builder.addCase(fetchCoupons.fulfilled, (state, action) => {
      const coupons = action.payload?.coupons;
      state.couponsPending = false;
      state.coupons = Array.isArray(coupons) ? coupons.filter((coupon) => coupon.code.length) : [];
      state.promoCode.isLimitReached = !action.payload?.canApplyCoupons;
    });

    builder.addCase(fetchCoupons.rejected, (state, action) => {
      state.couponsPending = false;
      state.couponErrors = [action.error.message || ""];
    });

    builder.addCase(applyCoupons.pending, (state) => {
      state.couponsPending = true;
      state.couponErrors = [];
    });

    builder.addCase(applyCoupons.fulfilled, (state, action) => {
      const coupons = action.payload?.appliedCoupons;
      state.couponsPending = false;
      state.coupons = Array.isArray(coupons) ? coupons.filter((coupon) => coupon.code.length) : [];
    });

    builder.addCase(applyCoupons.rejected, (state, action) => {
      state.couponsPending = false;
      state.couponErrors = [action.payload as string];
    });

    builder.addCase(removeCoupons.pending, (state) => {
      state.couponsPending = true;
      state.couponErrors = [];
    });

    builder.addCase(removeCoupons.fulfilled, (state, action) => {
      state.couponsPending = false;
      state.coupons = state.coupons.filter((coupon) => !action.payload.includes(coupon.code));
    });

    builder.addCase(removeCoupons.rejected, (state, action) => {
      state.couponsPending = false;
      state.couponErrors = [action.error.message || ""];
    });

    builder.addCase(placeOrder.fulfilled, (state) => {
      // state.page = "order";
      state.items = [];
    });

    builder.addCase(fetchCurrency.pending, (state) => {
      state.fetchCurrencyStatus = "pending";
    });

    builder.addCase(fetchCurrency.fulfilled, (state, action) => {
      state.currency = fromMagentoCurrency(action.payload);
      state.fetchCurrencyStatus = "success";
    });

    builder.addCase(fetchCurrency.rejected, (state) => {
      state.fetchCurrencyStatus = "error";
    });

    builder.addCase(fetchStoreConfig.pending, (state) => {
      state.fetchStoreConfigStatus = "pending";
    });

    builder.addCase(fetchStoreConfig.fulfilled, (state, action) => {
      state.storeConfig = action.payload as CartStoreConfig;
      state.fetchStoreConfigStatus = "success";
    });

    builder.addCase(fetchStoreConfig.rejected, (state) => {
      state.fetchStoreConfigStatus = "error";
    });

    builder.addCase(addProductToCart.fulfilled, (state, action) => {
      messageBus.publish("success.cart.add", { payload: { requestId: action.meta.arg.requestId } });
    });

    builder.addCase(addProductToCart.rejected, (state, action) => {
      action.payload &&
        messageBus.publish("fail.cart.add", {
          payload: { message: parseAddToCartErrorMessage(action.payload), requestId: action.meta.arg.requestId },
        });
    });
  },
});

export const {
  clearCouponErrors,
  setIsZeroValueOrder,
  setIsFailedPaymentFlow,
  setIsFreeTrial,
  setTradeInErrorMessage,
  setFreeGiftsErrors,
  clearFreeGiftsErrors,
  setDiscontinuedProductsErrors,
  clearDiscontinuedProductsErrors,
} = cartSlice.actions;
